import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { Badge, Button, Card, CardContent, Tabs, TabsList, TabsTrigger } from '@mlm/ui';
import { getFeedback, updateFeedbackStatus, type FeedbackRow } from '../lib/api';

type StatusFilter = 'all' | 'new' | 'read' | 'archived';

const CATEGORY_LABELS: Record<string, string> = {
  suggestion: 'Suggestion',
  complaint: 'Complaint',
  praise: 'Praise',
  bug: 'Problem',
  other: 'Other',
};

function StatusBadge({ status }: { status: FeedbackRow['status'] }) {
  const tone = status === 'new' ? 'default' : status === 'archived' ? 'secondary' : 'outline';
  return <Badge variant={tone as never}>{status}</Badge>;
}

function FeedbackCard({ row }: { row: FeedbackRow }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (status: FeedbackRow['status']) => updateFeedbackStatus(row.id, status),
    meta: { successMessage: 'Feedback updated.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-feedback'] }),
  });
  return (
    <Card>
      <CardContent className="space-y-2 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{CATEGORY_LABELS[row.category] ?? row.category}</Badge>
          <StatusBadge status={row.status} />
          {row.rating != null && (
            <span className="flex items-center gap-0.5 text-[#f5a623]">
              {Array.from({ length: row.rating }).map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5" fill="currentColor" />
              ))}
            </span>
          )}
          <span className="ml-auto text-xs text-muted-foreground">
            {new Date(row.createdAt).toLocaleString()}
          </span>
        </div>
        {row.subject && <div className="font-medium">{row.subject}</div>}
        <p className="whitespace-pre-line text-sm text-muted-foreground">{row.message}</p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {row.name && <span>{row.name}</span>}
          {row.email && <span>· {row.email}</span>}
          {!row.name && !row.email && <span>Anonymous</span>}
        </div>
        <div className="flex gap-2 pt-1">
          {row.status !== 'read' && (
            <Button
              size="sm"
              variant="outline"
              disabled={mut.isPending}
              onClick={() => mut.mutate('read')}
            >
              Mark read
            </Button>
          )}
          {row.status !== 'archived' && (
            <Button
              size="sm"
              variant="ghost"
              disabled={mut.isPending}
              onClick={() => mut.mutate('archived')}
            >
              Archive
            </Button>
          )}
          {row.status === 'archived' && (
            <Button
              size="sm"
              variant="ghost"
              disabled={mut.isPending}
              onClick={() => mut.mutate('new')}
            >
              Restore
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function FeedbackPage() {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const q = useQuery({
    queryKey: ['admin-feedback', filter],
    queryFn: () => getFeedback(filter === 'all' ? {} : { status: filter }),
  });
  const items = q.data?.items ?? [];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Feedback</h1>
      <p className="text-sm text-muted-foreground">
        Member and shopper feedback sent to the marketplace owner.
      </p>
      <Tabs value={filter} onValueChange={(v) => setFilter(v as StatusFilter)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="new">New</TabsTrigger>
          <TabsTrigger value="read">Read</TabsTrigger>
          <TabsTrigger value="archived">Archived</TabsTrigger>
        </TabsList>
      </Tabs>
      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No feedback yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((row) => (
            <FeedbackCard key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}
